/**
 * Transport transparency helpers (NET-REL-006) — labels + tooltips describing
 * the channel a message actually traveled over, in delivery priority order:
 * peer mesh → private Nostr relays → community (public mesh) → Supabase.
 */

import type { MessageEvent, TransportChannel } from "@veto/domain";

export type TransportTone = "good" | "warn" | "bad" | "idle";

export interface TransportInfo {
  /** Short badge label shown next to the timestamp. */
  label: string;
  /** Full tooltip text: channel + encryption status. */
  detail: string;
  tone: TransportTone;
}

const E2EE = "End-to-end encrypted (X25519 + ML-KEM hybrid)";

export function describeTransport(channel: TransportChannel | undefined): TransportInfo {
  switch (channel) {
    case "direct":
      return {
        label: "Direct",
        detail: `Peer mesh — sent over an open WebRTC DataChannel straight to the recipient. ${E2EE}.`,
        tone: "good",
      };
    case "direct+relay":
      return {
        label: "Direct+Relay",
        detail: `Peer mesh + private Nostr relays — delivered both ways for redundancy. ${E2EE}.`,
        tone: "good",
      };
    case "relay":
      return {
        label: "Relay",
        detail: `Private Nostr relays — authenticated, ciphertext-only store-and-forward. ${E2EE}.`,
        tone: "good",
      };
    case "expanded":
      return {
        label: "Relay+",
        detail: `Expanded relay routes — every approved relay was used after the top relays failed. ${E2EE}.`,
        tone: "good",
      };
    case "community":
      return {
        label: "Public",
        detail: `Public mesh (community relays) — different privacy boundary: community operators can see transport metadata (sender prefix, timing, size). Content stays ${E2EE.toLowerCase()}.`,
        tone: "warn",
      };
    case "realtime":
      return {
        label: "Fallback",
        detail: `Supabase realtime fallback — last-resort route when no relay was reachable. Non-preferred transport. Content stays ${E2EE.toLowerCase()}.`,
        tone: "bad",
      };
    default:
      return {
        label: "",
        detail: `Encrypted in transit and at rest. ${E2EE}.`,
        tone: "idle",
      };
  }
}

export function transportTooltip(msg: MessageEvent): string {
  const info = describeTransport(msg.transport);
  const status =
    msg.status === "backed_up"
      ? "Backed up (redundant routes confirmed)"
      : msg.status === "sent"
        ? "Sent (at least one route accepted)"
        : msg.status === "delivered"
          ? "Delivered (recipient device acknowledged)"
          : msg.status === "read"
            ? "Read"
            : "Queued — will send automatically when a route is available";
  return info.label ? `${info.detail}\n\nStatus: ${status}` : `${info.detail}\n${status}`;
}

export function securitySheetLines(msg: MessageEvent): string[] {
  const info = describeTransport(msg.transport);
  return [
    `Encryption: ${E2EE}, sealed locally before send.`,
    info.label ? `Transport: ${info.label} — ${info.detail}` : info.detail,
    `Delivery state: ${msg.status}.`,
    `Event id: ${msg.eventId}`,
    `Sealed at: ${new Date(msg.createdAt).toLocaleString()}`,
  ];
}
